/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

if (!customElements.get('warranty-form')) customElements.define('warranty-form', class extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.status = this.querySelector('[data-status]');
    const registration = this.querySelector('[data-registration]');
    if (registration) {
      registration.elements.country.value = 'United Kingdom';
      registration.elements.date_purchased.max = new Date().toISOString().slice(0, 10);
      const product = registration.elements.product, handpiece = this.querySelector('[data-handpiece]');
      product.addEventListener('change', () => {
        const enabled = product.value === 'Go File IQ';
        handpiece.hidden = !enabled;
        handpiece.querySelector('input').disabled = !enabled;
        handpiece.querySelector('input').required = enabled;
      });
      registration.addEventListener('submit', event => this.register(event));
    }
    this.querySelectorAll('[data-device]').forEach(button => button.addEventListener('click', () => {
      this.querySelectorAll('[data-device]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      this.querySelector('[data-verify] input[name=serial]').focus();
    }));
    this.querySelector('[data-verify]')?.addEventListener('submit', event => this.verify(event));
    this.querySelector('[data-support]')?.addEventListener('submit', event => this.support(event));
    this.querySelector('[data-verify] input[name=serial]')?.addEventListener('input', () => this.clearRegistration());
  }
  message(text, error = false) {
    this.status.textContent = text; this.status.dataset.error = String(error); this.status.focus();
  }
  clearRegistration() {
    this.registration = null; this.supportAttempt = null;
    this.querySelector('[data-support]').hidden = true;
    this.querySelector('[data-registration-summary]').hidden = true;
    this.querySelector('[data-not-found]').hidden = true;
  }
  async request(operation, data, form) {
    if (this.busy) return null;
    const endpoint = this.dataset.endpoint;
    if (!/^\/apps\/[a-z0-9-]+\/?$/i.test(endpoint)) throw new Error('The warranty service is not configured. Please contact support.');
    this.busy = true;
    const buttons = [...this.querySelectorAll('button[type=submit]')];
    buttons.forEach(button => button.disabled = true);
    this.message('Sending…');
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 25000);
    try {
      const response = await fetch(`${endpoint.replace(/\/$/, '')}/${operation}`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(data),signal:controller.signal});
      const result = await response.json().catch(() => { throw new Error('The warranty service is temporarily unavailable. Your details have been kept; please try again.'); });
      if (!response.ok) { const error = new Error(result.error || 'Unable to submit. Please check your details and try again.'); error.status = response.status; throw error; }
      return result;
    } catch (error) {
      if (error.name === 'AbortError' || error instanceof TypeError) throw new Error('We could not confirm receipt. Your details have been kept; retry to check the same submission.');
      throw error;
    } finally { clearTimeout(timer); this.busy = false; buttons.forEach(button => button.disabled = false); }
  }
  async register(event) {
    event.preventDefault(); const form = event.currentTarget;
    try {
      const result = await this.request('registrations', Object.fromEntries(new FormData(form)), form);
      if (!result) return;
      if (!result.id) throw new Error('Unable to confirm your registration. Please retry.');
      form.hidden = true;
      this.message(`Your product registration has been received. Reference: ${result.id}. ${result.confirmation === 'sent' ? 'A confirmation email has been sent.' : 'Your confirmation email will follow.'}`);
    } catch (error) { this.message(error.message, true); }
  }
  async verify(event) {
    event.preventDefault(); const form = event.currentTarget;
    this.clearRegistration();
    try {
      const result = await this.request('verify', Object.fromEntries(new FormData(form)), form);
      if (!result) return;
      if (!result.found) { this.message('No registration found.'); this.querySelector('[data-not-found]').hidden = false; return; }
      const entry = result.registration;
      if (!entry?.id || !entry?.serial_number) throw new Error('Unable to verify your registration. Please retry.');
      this.registration = entry;
      const details = this.querySelector('[data-registration-details]'); details.replaceChildren();
      const labels = {product:'Product',serial_number:'Serial number',date_purchased:'Purchased',date_entered:'Registered',expires_on:'Recorded expiry',status:'Registration status'};
      for (const [field, label] of Object.entries(labels)) { const dt=document.createElement('dt'),dd=document.createElement('dd');dt.textContent=label;dd.textContent=String(entry[field] || '').replaceAll('_',' ');details.append(dt,dd); }
      this.querySelector('[data-registration-summary]').hidden = false;
      this.querySelector('[data-support]').hidden = false;
      this.message('Registration found. Tell us how we can help.');
    } catch (error) { this.message(error.message, true); }
  }
  async support(event) {
    event.preventDefault(); const form = event.currentTarget;
    if (!this.registration) return;
    const data = {...Object.fromEntries(new FormData(form)),registrationId:this.registration.id,productLink:this.registration.serial_number};
    const fingerprint = JSON.stringify(data);
    if (this.supportAttempt && fingerprint !== this.supportAttempt.fingerprint) {
      this.message('A previous attempt may have been received. Restore the previous details and retry before starting a different request.', true); return;
    }
    this.supportAttempt ||= {fingerprint,requestId:crypto.randomUUID()};
    try {
      const result = await this.request('support', {...data,requestId:this.supportAttempt.requestId}, form);
      if (!result) return;
      if (!result.id) throw new Error('Unable to confirm your support request. Please retry.');
      this.supportAttempt = null; form.hidden = true;
      this.message(`Your support request has been received. Reference: ${result.id}. Our support team will review your request. This is not a repair authorisation.`);
    } catch (error) { if ([400,404,422].includes(error.status)) this.supportAttempt = null; this.message(error.message, true); }
  }
});
